'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

import SiteHeader from '@/components/SiteHeader';
import { apiClient } from '@/lib/api';
import { GAME_CATALOG, type GameCatalogEntry } from '@/lib/games/catalog';
import { resolveMysteryRoomDailySeed } from '@/lib/games/mysteryRoom/engine';

type LeaderboardEntry = { name: string; score: number; avatar?: string | null };

export default function GamesPage() {
  const [records, setRecords] = useState<Record<string, { global: number; top: LeaderboardEntry[] }>>({});

  useEffect(() => {
    const load = async () => {
      const result: Record<string, { global: number; top: LeaderboardEntry[] }> = {};
      const mysterySeed = resolveMysteryRoomDailySeed();

      await Promise.all(
        GAME_CATALOG.map(async (game) => {
          try {
            const bestUrl =
              game.id === 'mystery-room'
                ? `/api/games/${game.apiKey}/best/?seed=${encodeURIComponent(mysterySeed)}`
                : `/api/games/${game.apiKey}/best/`;
            const leaderboardUrl =
              game.id === 'mystery-room'
                ? `/api/games/${game.apiKey}/leaderboard/?seed=${encodeURIComponent(mysterySeed)}&limit=3`
                : `/api/games/${game.apiKey}/leaderboard/?limit=3`;
            const [bestData, lbData] = await Promise.all([
              apiClient.request<{ global_best?: number }>(bestUrl),
              game.leaderboardPreview
                ? apiClient.request<{ leaderboard?: LeaderboardEntry[] }>(leaderboardUrl)
                : Promise.resolve(null),
            ]);

            result[game.id] = {
              global: Number(bestData?.global_best) || 0,
              top: (lbData as { leaderboard?: LeaderboardEntry[] } | null)?.leaderboard || [],
            };
          } catch {
            result[game.id] = { global: 0, top: [] };
          }
        }),
      );

      setRecords(result);
    };

    void load();
  }, []);

  return (
    <div className="min-h-screen bg-site-bg">
      <SiteHeader />

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <h1 className="mb-2 text-3xl font-bold text-site-text">Игры</h1>
        <p className="mb-8 text-site-muted">Научные мини-игры лаборатории ЛИСАД</p>

        <div className="grid gap-4 sm:grid-cols-2">
          {GAME_CATALOG.map((game) => {
            const rec = records[game.id];

            return (
              <Link
                key={game.id}
                href={game.href}
                className="group block rounded-2xl border border-site-border/40 bg-site-bg/50 p-5 transition-all hover:border-site-border-control/60 hover:bg-site-bg/80"
              >
                <div className="flex items-start gap-4">
                  <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${game.gradient} text-site-text shadow-lg`}>
                    {renderGameIcon(game)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-lg font-semibold text-site-text transition-colors group-hover:text-site-accent">{game.title}</div>
                    <p className="mt-1 line-clamp-2 text-sm text-site-muted">{game.description}</p>

                    {rec && rec.global > 0 && (
                      <div className="mt-3 text-xs text-site-muted">
                        {game.scoreScope === 'daily' ? 'Рекорд дня' : 'Рекорд'}: <span className="font-semibold text-site-text">{rec.global}</span>
                      </div>
                    )}
                    {rec && rec.top.length > 0 && (
                      <div className="mt-1.5 flex gap-3 text-xs text-site-muted">
                        {rec.top.map((player, index) => (
                          <span key={`${player.name}-${index}`}>
                            {index + 1}. <span className="text-site-secondary">{player.name}</span> — {player.score}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function renderGameIcon(game: GameCatalogEntry) {
  if (game.iconKey === 'staff-runner') {
    return (
      <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3h6l-1 6h4L10 21l1-8H7l2-10z" />
      </svg>
    );
  }

  if (game.iconKey === 'mystery-room') {
    return (
      <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" d="M4 9.5 12 4l8 5.5v9A1.5 1.5 0 0 1 18.5 20h-13A1.5 1.5 0 0 1 4 18.5v-9Z" />
        <path strokeWidth={1.5} strokeLinecap="round" d="M9.5 11.5a2.5 2.5 0 1 1 4.3 1.7c-.5.5-1.3 1-1.3 2" />
        <circle cx="12" cy="17.2" r="0.8" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  return (
    <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="5" cy="6" r="1.5" strokeWidth={1.5} />
      <circle cx="5" cy="12" r="1.5" strokeWidth={1.5} />
      <circle cx="5" cy="18" r="1.5" strokeWidth={1.5} />
      <circle cx="12" cy="8" r="1.5" strokeWidth={1.5} />
      <circle cx="12" cy="16" r="1.5" strokeWidth={1.5} />
      <circle cx="19" cy="12" r="1.5" strokeWidth={1.5} />
      <path strokeWidth={1} d="M6.5 6l4 1.5M6.5 12l4-2.5M6.5 12l4 2.5M6.5 18l4-3.5M13.5 8l4 3M13.5 16l4-3" />
    </svg>
  );
}
